import type { FailureScope, FailureSummary, ReplicaFailures, TursoWorkerDatabaseDriver } from '@w/db-turso-browser-adapter/worker'
import type { SpaceResource } from '../router/spaces-resources'
import type { Tel } from '@w/tel'
import { safe } from '@w/utils'

export type ItemReadResource = Pick<SpaceResource, 'db' | 'userId' | 'connectionId'> & { space: Pick<SpaceResource['space'], 'workspaceId'> }

/** Read retained native scope; commands authenticate it against live principal. */
export async function failureScope(resource: ItemReadResource): Promise<FailureScope> {
  const [state] = await safe(resource.db.driver).query<{ failure_workspace: string; failure_principal: string; failure_generation: number | bigint; identity: Uint8Array }>(
    'SELECT failure_workspace,failure_principal,failure_generation,identity FROM _turso_replication_state WHERE id=1',
  )
  const row = safe(state, 'Native failure scope required')
  if (row.failure_workspace !== resource.space.workspaceId || row.failure_principal !== resource.userId || row.failure_generation === null) throw new Error('Workspace failure scope changed')
  return { workspace: row.failure_workspace, principal: resource.userId, generation: BigInt(row.failure_generation), replica: new Uint8Array(row.identity) }
}

export function nativeFailures(resource: Pick<SpaceResource, 'db'>): ReplicaFailures {
  return safe((resource.db.driver as TursoWorkerDatabaseDriver).failures, 'Native failure commands required')
}

export function failureListOptions(resource: SpaceResource, tel: Tel) {
  return {
    queryKey: ['workspace-failures', resource.userId, resource.space.workspaceId, resource.connectionId], staleTime: Infinity,
    queryFn: async () => {
      // Reads record nothing; `read` rows left by older builds are not failures.
      const entries = (await nativeFailures(resource).list(await failureScope(resource))).filter(entry => entry.category !== 'read')
      await tel.span({ name: 'workspace.item-failures', type: 'query', trace: true, input: { workspaceId: resource.space.workspaceId } }, diagnosisTel => {
        const groups = new Map<string, { code: string; category: string; direction: string; count: number }>()
        for (const entry of entries.filter(entry => entry.status === 'failed')) {
          const category = ['derived', 'inbound', 'outbound'].includes(entry.category) ? entry.category : 'item'
          const direction = category === 'inbound' || category === 'outbound' ? category : 'local'
          const codes = new Set(entry.reasons.map(reason => {
            const candidate = reason.replace(/^\d+:\s*/, '')
            return Object.hasOwn(reasons, candidate) ? candidate : 'ITEM_FAILURE'
          }))
          for (const code of codes) {
            const key = `${category}:${code}`
            const group = groups.get(key) ?? { code, category, direction, count: 0 }
            group.count += 1
            groups.set(key, group)
          }
        }
        for (const group of groups.values()) diagnosisTel.info('Workspace item failures', { workspaceId: resource.space.workspaceId, ...group })
      })
      return entries
    },
    subscribe: (invalidate: () => void) => subscribeFailures(resource, invalidate, tel),
  }
}

/** Source access may change without changing a letter; revalidate permitted identities too. */
export async function subscribeFailures(resource: SpaceResource, invalidate: () => void, tel: Tel) {
  const releaseNative = await nativeFailures(resource).subscribe(await failureScope(resource), invalidate, error => {
    tel.error(error, { operation: 'workspace.failures.subscription', workspaceId: resource.space.workspaceId })
    invalidate()
  })
  try {
    const releaseSource = await (safe(resource.db.driver) as TursoWorkerDatabaseDriver).subscribe('SELECT id FROM pages', invalidate)
    return () => { try { releaseSource() } finally { releaseNative() } }
  } catch (error) { releaseNative(); throw error }
}

// Fixed diagnostics only; never echo arbitrary exceptions or retained bytes.
const reasons: Record<string, string> = {
  ROW_PERMISSION_DENIED: 'Row permission denied', TRANSACTION_REFUSED: 'Transaction refused', PAYLOAD_TOO_LARGE: 'Transaction too large',
  OWNER_CARD_DERIVATION: 'Card update unavailable', PRODUCT_CONTENT_CONSTRAINT: 'Content could not be applied',
  MALFORMED_CONTENT: 'Content unavailable', MISSING_CONTENT_DEPENDENCY: 'Content dependency unavailable',
}
export function failureReason(reason: string): string {
  const code = reason.replace(/^\d+:\s*/, '')
  return reasons[code] ?? 'Item could not be processed'
}
export type { FailureSummary }
